import { useEffect, useRef } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { MsalProvider, useMsal } from '@azure/msal-react'
import { PublicClientApplication } from '@azure/msal-browser'
import { resolveRedirectUri } from './auth/redirectUri'
import { AuthProvider } from './context/AuthContext'
import { useAuth } from './context/AuthContext'
import { getMyProfileWithToken } from './services/api'
import Layout from './components/Layout'
import { ToastProvider } from './components/ToastProvider'
import ProtectedRoute from './components/ProtectedRoute'
import Dashboard from './pages/Dashboard'
import Targets from './pages/Targets'
import Distributors from './pages/Distributors'
import CheckRunsList from './pages/CheckRunsList'
import CheckRunDetail from './pages/CheckRunDetail'
import Findings from './pages/Findings'
import Actions from './pages/Actions'
import Settings from './pages/Settings'
import AuditLog from './pages/AuditLog'
import MimSetup from './pages/MimSetup'
import RulePacks from './pages/RulePacks'
import RulePackDetail from './pages/RulePackDetail'
import ExtractionProfileDetail from './pages/ExtractionProfileDetail'
import ExtractionProfileGenerate from './pages/ExtractionProfileGenerate'
import Products from './pages/Products'
import PCRUpload from './pages/PCRUpload'
import ProductDetail from './pages/ProductDetail'
import ProductPimManualEditor from './pages/ProductPimManualEditor'
import ProductCompare from './pages/ProductCompare'
import ProductComplianceScopePage from './pages/ProductComplianceScopePage'
import OrphanedProducts from './pages/OrphanedProducts'
import RunChecks from './pages/RunChecks'
import Login from './pages/Login'
import Users from './pages/Users'
import DivisionManagement from './pages/DivisionManagement'
import MasterTerms from './pages/MasterTerms'
import TermWatches from './pages/TermWatches'
import DistributorSearch from './pages/DistributorSearch'
import DistributorActionReports from './pages/DistributorActionReports'
import SupportTickets from './pages/SupportTickets'
import Observability from './pages/Observability'
import HealthChecks from './pages/HealthChecks'
import TestRunner from './pages/TestRunner'
import ComplianceAuditHub from './pages/ComplianceAuditHub'
import AdminDataReset from './pages/AdminDataReset'
import AdminComplianceWorkflow from './pages/AdminComplianceWorkflow'
import Scheduler from './pages/Scheduler'
import MasterAdminRoute from './components/MasterAdminRoute'

// Initialize MSAL (Azure AD authentication)
const legacyClientId = (import.meta as any).env?.VITE_CLIENT_ID
const legacyTenantId = (import.meta as any).env?.VITE_TENANT_ID

function resolveApiScope(): string | undefined {
  const explicitScope = import.meta.env.VITE_AZURE_API_SCOPE?.trim()
  if (explicitScope) return explicitScope

  const apiClientId = import.meta.env.VITE_AZURE_API_CLIENT_ID?.trim()
  if (apiClientId) return `api://${apiClientId}/.default`

  return undefined
}

const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || legacyClientId || 'your-client-id',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID || legacyTenantId || 'common'}`,
    redirectUri: resolveRedirectUri(),
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
}

const pca = new PublicClientApplication(msalConfig)

function MsalAuthBridge() {
  const { instance, accounts } = useMsal()
  const { token, isAuthenticated, login } = useAuth()
  const didHandleRedirectRef = useRef(false)

  useEffect(() => {
    // Ensure MSAL consumes the redirect response (#code=... etc.) exactly once.
    // Also, ensure MSAL is initialized before calling MSAL APIs.
    if (didHandleRedirectRef.current) return
    didHandleRedirectRef.current = true

    ;(async () => {
      try {
        const initFn = (instance as any)?.initialize
        if (typeof initFn === 'function') {
          await initFn.call(instance)
        }

        const result = await instance.handleRedirectPromise()
        if (result?.account) {
          instance.setActiveAccount(result.account)
        }
      } catch (err) {
        console.warn('MSAL handleRedirectPromise failed', err)
      }
    })()
  }, [instance])

  useEffect(() => {
    if (isAuthenticated || token) return
    if (sessionStorage.getItem('mimgrc_sso_logout') === 'true') return
    const account = accounts[0]
    if (!account) return

    const apiScope = resolveApiScope()

    if (!apiScope) {
      console.warn('MSAL bridge skipped: configure VITE_AZURE_API_SCOPE or VITE_AZURE_API_CLIENT_ID')
      return
    }

    instance
      .acquireTokenSilent({
        account,
        scopes: [apiScope],
      })
      .then(async (result) => {
        if (!result.accessToken) return
        // Require server-side profile fetch before marking the app as authenticated.
        // This prevents redirect loops when the token is invalid for API calls.
        try {
          const profile = await getMyProfileWithToken(result.accessToken)
          login(result.accessToken, {
            userId: profile.userId,
            displayName: profile.displayName,
            email: profile.email,
            role: profile.role,
            businessLevel: profile.businessLevel,
            functionalRoles: profile.functionalRoles,
            divisionId: profile.divisionId,
            subdivisionId: profile.subdivisionId,
            businessEntityIds: profile.businessEntityIds,
            divisionName: profile.divisionName,
            subdivisionName: profile.subdivisionName,
            businessEntityNames: profile.businessEntityNames,
            isAutoProvisioned: profile.isAutoProvisioned,
            isProfileComplete: profile.isProfileComplete,
            authMethod: 'sso',
          })
        } catch (err) {
          localStorage.removeItem('mimgrc_token')
          localStorage.removeItem('mimgrc_user')
          console.warn('SSO token acquired but backend profile could not be loaded; auth state was not applied.', err)
        }
      })
      .catch((err) => {
        const rawMessage =
          typeof err?.message === 'string'
            ? err.message
            : typeof err?.errorMessage === 'string'
              ? err.errorMessage
              : ''
        if (rawMessage.includes('AADSTS500011') || rawMessage.includes('invalid_resource')) {
          console.error(
            'Entra resource misconfiguration: requested API scope is not resolvable in this tenant. Check VITE_AZURE_API_CLIENT_ID/VITE_AZURE_API_SCOPE and API app registration consent.',
          )
        }
        console.warn('Failed to acquire Entra access token for API', err)
      })
  }, [accounts, instance, isAuthenticated, token, login])

  return null
}

function App() {
  return (
    <MsalProvider instance={pca}>
      <AuthProvider>
        <MsalAuthBridge />
        <ToastProvider>
          <Router future={{ v7_relativeSplatPath: true }}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<Layout><ProtectedRoute><Dashboard /></ProtectedRoute></Layout>} />
              <Route path="/pcr-upload" element={<Layout><ProtectedRoute><PCRUpload /></ProtectedRoute></Layout>} />
              <Route path="/pcr-management" element={<Layout><ProtectedRoute><Products /></ProtectedRoute></Layout>} />
              <Route path="/compliance-check" element={<Layout><ProtectedRoute><RunChecks /></ProtectedRoute></Layout>} />
              <Route path="/scheduler" element={<Layout><ProtectedRoute><Scheduler /></ProtectedRoute></Layout>} />
              <Route path="/exception-report" element={<Layout><ProtectedRoute><Findings /></ProtectedRoute></Layout>} />
              <Route path="/check-history" element={<Layout><ProtectedRoute><CheckRunsList /></ProtectedRoute></Layout>} />
              <Route path="/rules-engine" element={<Layout><ProtectedRoute><RulePacks /></ProtectedRoute></Layout>} />
              <Route path="/term-watcher" element={<Layout><ProtectedRoute><TermWatches /></ProtectedRoute></Layout>} />
              <Route path="/targets" element={<Layout><ProtectedRoute><Targets /></ProtectedRoute></Layout>} />
              <Route path="/distributors" element={<Layout><ProtectedRoute><Distributors /></ProtectedRoute></Layout>} />
              <Route path="/compliance-audit" element={<Layout><ProtectedRoute><MasterAdminRoute><ComplianceAuditHub /></MasterAdminRoute></ProtectedRoute></Layout>} />
              <Route path="/products" element={<Layout><ProtectedRoute><Products /></ProtectedRoute></Layout>} />
              <Route path="/products/:productId" element={<Layout><ProtectedRoute><ProductDetail /></ProtectedRoute></Layout>} />
              <Route path="/products/:productId/details" element={<Layout><ProtectedRoute><ProductDetail /></ProtectedRoute></Layout>} />
              <Route path="/products/:productId/pim-manual" element={<Layout><ProtectedRoute><ProductPimManualEditor /></ProtectedRoute></Layout>} />
              <Route path="/products/:productId/compliance-scope" element={<Layout><ProtectedRoute><ProductComplianceScopePage /></ProtectedRoute></Layout>} />
              <Route path="/products/:productId/compare" element={<Layout><ProtectedRoute><ProductCompare /></ProtectedRoute></Layout>} />
              <Route path="/products/orphaned" element={<Layout><ProtectedRoute><OrphanedProducts /></ProtectedRoute></Layout>} />
              <Route path="/run-checks" element={<Layout><ProtectedRoute><RunChecks /></ProtectedRoute></Layout>} />
              <Route path="/check-runs" element={<Layout><ProtectedRoute><CheckRunsList /></ProtectedRoute></Layout>} />
              <Route path="/check-runs/:checkRunId" element={<Layout><ProtectedRoute><CheckRunDetail /></ProtectedRoute></Layout>} />
              <Route path="/findings" element={<Layout><ProtectedRoute><Findings /></ProtectedRoute></Layout>} />
              <Route path="/rules" element={<Layout><ProtectedRoute><RulePacks /></ProtectedRoute></Layout>} />
              <Route path="/rules/:rulePackId" element={<Layout><ProtectedRoute><RulePackDetail /></ProtectedRoute></Layout>} />
              <Route path="/extraction-profiles/generate" element={<Layout><ProtectedRoute><ExtractionProfileGenerate /></ProtectedRoute></Layout>} />
              <Route path="/extraction-profiles/:profileId" element={<Layout><ProtectedRoute><ExtractionProfileDetail /></ProtectedRoute></Layout>} />
              <Route path="/actions" element={<Layout><ProtectedRoute><Actions /></ProtectedRoute></Layout>} />
              <Route path="/distributor-action-reports" element={<Layout><ProtectedRoute><DistributorActionReports /></ProtectedRoute></Layout>} />
              <Route path="/settings" element={<Layout><ProtectedRoute><Settings /></ProtectedRoute></Layout>} />
              <Route path="/users" element={<Layout><ProtectedRoute><Users /></ProtectedRoute></Layout>} />
              <Route path="/admin/divisions" element={<Layout><ProtectedRoute><MasterAdminRoute><DivisionManagement /></MasterAdminRoute></ProtectedRoute></Layout>} />
              <Route path="/master-terms" element={<Layout><ProtectedRoute><MasterTerms /></ProtectedRoute></Layout>} />
              <Route path="/audit" element={<Layout><ProtectedRoute><AuditLog /></ProtectedRoute></Layout>} />
              <Route path="/pim" element={<Layout><ProtectedRoute><MimSetup /></ProtectedRoute></Layout>} />
              <Route path="/term-watches" element={<Layout><ProtectedRoute><TermWatches /></ProtectedRoute></Layout>} />
              <Route path="/distributors/:distributorId/search" element={<Layout><ProtectedRoute><DistributorSearch /></ProtectedRoute></Layout>} />
              <Route path="/support-tickets" element={<Layout><ProtectedRoute><SupportTickets /></ProtectedRoute></Layout>} />
              <Route path="/observability" element={<Layout><ProtectedRoute><Observability /></ProtectedRoute></Layout>} />
              <Route path="/health-checks" element={<Layout><ProtectedRoute><HealthChecks /></ProtectedRoute></Layout>} />
              <Route path="/test-runner" element={<Layout><ProtectedRoute><MasterAdminRoute><TestRunner /></MasterAdminRoute></ProtectedRoute></Layout>} />
              <Route path="/admin/compliance-workflow" element={<Layout><ProtectedRoute><MasterAdminRoute><AdminComplianceWorkflow /></MasterAdminRoute></ProtectedRoute></Layout>} />
              <Route path="/admin/data-reset" element={<Layout><ProtectedRoute><MasterAdminRoute><AdminDataReset /></MasterAdminRoute></ProtectedRoute></Layout>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </MsalProvider>
  )
}

export default App
